import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  DEFAULT_SETTINGS, SESSION_MODES, SESSION_MODE_HINT, SESSION_MODE_LABEL,
  type AssignPriority, type DeployNowBehavior, type SessionMode,
} from '@micplot/shared';
import { api, type ShowOwner } from '../lib/api';
import ShowReport from './ShowReport';
import { useStore } from '../state/store';
import ConfirmDialog from './ConfirmDialog';

interface OwnersTabProps {
  code: string;
  owners: ShowOwner[] | null;
  setOwners: (o: ShowOwner[]) => void;
  email: string;
  setEmail: (e: string) => void;
  notify: (text: string, kind?: 'info' | 'error') => void;
}

/**
 * Co-owners can run the show without the creator present -- useful when more
 * than one person produces, or when someone is unreachable mid-run. Only the
 * creator can grant or revoke it, and the creator cannot be removed.
 */
function OwnersTab({ code, owners, setOwners, email, setEmail, notify }: OwnersTabProps) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    setLoaded(true);
    api.listOwners(code)
      .then(setOwners)
      .catch(err => notify((err as Error).message, 'error'));
  }, [code, loaded, setOwners, notify]);

  const creator = owners?.find(o => o.isCreator);

  return (
    <>
      <div className="field">
        <label>Who can run this show</label>
        <div className="hint" style={{ marginBottom: 10 }}>
          Owners can open, edit and run this show from their own account, with or
          without the code. Crew still join with the code and need no account.
        </div>
        {owners === null ? (
          <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {owners.map(o => (
              <div className="row" key={o.userId}>
                <strong>{o.displayName}</strong>
                <span className="muted" style={{ fontSize: 12 }}>{o.email}</span>
                {o.isCreator && <span className="chip">creator</span>}
                <div className="header-spacer" />
                {!o.isCreator && (
                  <button className="btn sm" onClick={async () => {
                    try { setOwners(await api.removeOwner(code, o.userId)); }
                    catch (err) { notify((err as Error).message, 'error'); }
                  }}>Remove</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="field">
        <label htmlFor="coowner">Add a co-owner</label>
        <div className="row">
          <input id="coowner" type="email" value={email} placeholder="their@email.com"
                 onChange={e => setEmail(e.target.value)} />
          <button className="btn" disabled={!email.trim()} onClick={async () => {
            try {
              setOwners(await api.addOwner(code, email.trim()));
              setEmail('');
              notify('Co-owner added.');
            } catch (err) { notify((err as Error).message, 'error'); }
          }}>Add</button>
        </div>
        <div className="hint">
          They need an existing account. {creator ? `Only ${creator.displayName} can change this.` : ''}
        </div>
      </div>
    </>
  );
}

export default function SessionPanel({ onClose }: { onClose: () => void }) {
  const identity = useStore(s => s.identity)!;
  const setIdentity = useStore(s => s.setIdentity);
  const notify = useStore(s => s.notify);
  const state = useStore(s => s.state);
  const joinRequests = useStore(s => s.joinRequests);
  const dismissJoinRequest = useStore(s => s.dismissJoinRequest);

  const [tab, setTab] =
    useState<'code' | 'remote' | 'settings' | 'people' | 'show' | 'owners' | 'report'>('code');
  const showFileRef = useRef<HTMLInputElement>(null);
  const [showName, setShowName] = useState('');
  const [pendingShow, setPendingShow] = useState<{ file: unknown; label: string } | null>(null);
  const [remoteToken, setRemoteToken] = useState<string | null>(null);
  const [owners, setOwners] = useState<ShowOwner[] | null>(null);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [qr, setQr] = useState('');
  const [confirmEnd, setConfirmEnd] = useState(false);
  const settings = state?.session.settings ?? DEFAULT_SETTINGS;
  const isHost = identity.role === 'host';
  const joinUrl = `${location.origin}/?join=${identity.code}`;

  useEffect(() => {
    QRCode.toDataURL(joinUrl, { width: 220, margin: 1 })
      .then(setQr)
      .catch(() => setQr(''));
  }, [joinUrl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function patch(p: Parameters<typeof api.updateSettings>[1]) {
    try { await api.updateSettings(identity.code, p); }
    catch (err) { notify((err as Error).message, 'error'); }
  }

  // The token is fetched only when the host opens the Remote tab, so it is not
  // sitting in every client's memory.
  useEffect(() => {
    if (tab !== 'remote' || !isHost || remoteToken !== null) return;
    api.getRemoteToken(identity.code)
      .then(r => setRemoteToken(r.token))
      .catch(() => setRemoteToken(''));
  }, [tab, isHost, identity.code, remoteToken]);

  const companionUrl = (action: string) =>
    `${location.origin}/api/sessions/${identity.code}/playhead/${action}` +
    `?token=${remoteToken ?? 'YOUR-TOKEN'}`;

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label="Session">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>Show</h2>
          <button className="btn sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="sub">You are the {identity.role} of this show.</p>

        <div className="tabs" role="tablist">
          {(['code', 'people', 'owners', 'show', 'report', 'remote', 'settings'] as const).map(t => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>
              {t === 'code' ? 'Code'
                : t === 'people' ? `People${joinRequests.length
                  ? ` (${joinRequests.length})` : ''}`
                : t === 'owners' ? 'Owners'
                : t === 'show' ? 'Show file'
                : t === 'report' ? 'Report'
                : t === 'remote' ? 'Remote' : 'Settings'}
            </button>
          ))}
        </div>

        {tab === 'code' && (
          <>
            <div className="big-code">{identity.code}</div>
            <p className="sub" style={{ textAlign: 'center' }}>
              Share this code so the crew can join.
            </p>
            {qr && <div className="qr"><img src={qr} alt="Join QR code" width={220} /></div>}
            {isHost && (
              <div className="modal-actions">
                <button className="btn danger" onClick={() => setConfirmEnd(true)}>
                  End show
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'people' && (
          <>
            {joinRequests.length > 0 && (
              <>
                <label style={{ fontSize: 13, fontWeight: 550 }}>Waiting for approval</label>
                {joinRequests.map(p => (
                  <div className="banner" key={p.id}>
                    <strong>{p.name}</strong>
                    <span className="muted">wants editor access</span>
                    <div className="header-spacer" />
                    <button className="btn sm" onClick={async () => {
                      await api.approve(identity.code, p.id, false);
                      dismissJoinRequest(p.id);
                    }}>Deny</button>
                    <button className="btn sm primary" onClick={async () => {
                      await api.approve(identity.code, p.id, true);
                      dismissJoinRequest(p.id);
                      notify(`${p.name} can now edit.`);
                    }}>Approve</button>
                  </div>
                ))}
              </>
            )}
            <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
              {(state?.participants ?? []).map(p => (
                <div className="row" key={p.id}>
                  <span className={`dot ${p.online ? 'on' : ''}`} />
                  <strong>{p.name}</strong>
                  <span className="chip">{p.role}</span>
                  {p.status !== 'approved' && <span className="chip">{p.status}</span>}
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'owners' && (
          <OwnersTab
            code={identity.code}
            owners={owners}
            setOwners={setOwners}
            email={ownerEmail}
            setEmail={setOwnerEmail}
            notify={notify}
          />
        )}

        {tab === 'report' && <ShowReport />}

        {tab === 'show' && (
          <>
            <div className="field">
              <label htmlFor="showname">Save this show</label>
              <div className="hint" style={{ marginBottom: 8 }}>
                Saves the plot — presenters, on-stage times, channels, cues and settings —
                so the same production can be reloaded on the next performance.
                Tonight's live assignments are not included.
              </div>
              <div className="row">
                <input
                  id="showname" value={showName} placeholder="e.g. Hamlet — mic plot"
                  onChange={e => setShowName(e.target.value)}
                />
                <button className="btn primary" onClick={async () => {
                  try {
                    await api.exportShow(identity.code, showName.trim() || 'show');
                    notify('Show file downloaded.');
                  } catch (err) { notify((err as Error).message, 'error'); }
                }}>
                  Save
                </button>
              </div>
            </div>

            <div className="field">
              <label>Load a show</label>
              <div className="hint" style={{ marginBottom: 8 }}>
                Replaces everything currently on the board.
              </div>
              <button className="btn" disabled={identity.role === 'viewer'}
                      onClick={() => showFileRef.current?.click()}>
                Choose show file…
              </button>
              <input
                ref={showFileRef} type="file" hidden accept=".json,application/json"
                onChange={async e => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (!f) return;
                  try {
                    const parsed = JSON.parse(await f.text());
                    setPendingShow({
                      file: parsed,
                      label: typeof parsed?.name === 'string' ? parsed.name : f.name,
                    });
                  } catch {
                    notify('That file is not valid JSON.', 'error');
                  }
                }}
              />
            </div>
          </>
        )}

        {tab === 'remote' && (
          <>
            <div className="field">
              <label>Stream Deck / Bitfocus Companion</label>
              {!isHost ? (
                <div className="hint">
                  Only the host can see the remote-control link.
                </div>
              ) : (
                <>
                  <div className="hint" style={{ marginBottom: 8 }}>
                    Point a Companion generic-HTTP button at one of these.
                    Both GET and POST work, so almost any control surface can drive it.
                  </div>
                  {(['next', 'prev', 'deploy'] as const).map(action => (
                    <div className="row" key={action} style={{ marginBottom: 6 }}>
                      <span className="chip" style={{ width: 58 }}>{action}</span>
                      <input readOnly value={companionUrl(action)}
                             onFocus={e => e.currentTarget.select()}
                             style={{ fontFamily: 'var(--mono)', fontSize: 11 }} />
                    </div>
                  ))}
                  <div className="hint">
                    These links contain a secret that can drive the show. Keep them to
                    your own control surface — the session code alone deliberately
                    cannot advance cues, so viewers can't move the playhead.
                  </div>
                </>
              )}
            </div>
            <div className="field">
              <label>Phone</label>
              <div className="hint">
                Scan the join QR on the Code tab from a phone to carry the board backstage.
              </div>
            </div>
          </>
        )}

        {tab === 'settings' && (
          <>
            <div className="field">
              <label>How this show runs</label>
              <div className="seg">
                {SESSION_MODES.map(m => (
                  <button key={m} aria-pressed={(state?.session.mode ?? 'run-and-gun') === m}
                          onClick={async () => {
                            try { await api.setMode(identity.code, m); }
                            catch (err) { notify((err as Error).message, 'error'); }
                          }}>
                    <strong>{SESSION_MODE_LABEL[m as SessionMode]}</strong>
                    <div className="hint">{SESSION_MODE_HINT[m as SessionMode]}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label htmlFor="buffer">
                Changeover buffer — {settings.changeoverBufferMin} min
              </label>
              <input id="buffer" type="range" min={0} max={60} step={1}
                     value={settings.changeoverBufferMin}
                     disabled={!isHost && identity.role !== 'editor'}
                     onChange={e => patch({ changeoverBufferMin: Number(e.target.value) })} />
              <div className="hint">
                Minimum gap before a lav or headset can move to the next presenter on the
                same channel. Handhelds ignore this — they can be passed straight across.
              </div>
            </div>

            <div className="field">
              <label>Auto-assign priority</label>
              <div className="seg">
                {([
                  ['spread', 'Spread across channels',
                   'Use unused channels first. Safer when the running order slips.'],
                  ['reuse', 'Pack onto fewest channels',
                   'Reuse channels aggressively. Keeps spare RF free.'],
                ] as [AssignPriority, string, string][]).map(([v, label, hint]) => (
                  <button key={v} aria-pressed={settings.assignPriority === v}
                          onClick={() => patch({ assignPriority: v })}>
                    <strong>{label}</strong>
                    <div className="hint">{hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>Auto-deploy</label>
              <div className="seg">
                <button aria-pressed={settings.autoDeploy}
                        onClick={() => patch({ autoDeploy: !settings.autoDeploy })}>
                  <strong>{settings.autoDeploy ? 'On' : 'Off'}</strong>
                  <div className="hint">
                    {settings.autoDeploy
                      ? 'Changes go live as the playhead advances.'
                      : 'Changes stay staged until you hit Deploy.'}
                  </div>
                </button>
              </div>
            </div>

            <div className="field">
              <label>Deploy Now while auto-deploy is on</label>
              <div className="seg">
                {([
                  ['disable-autodeploy', 'Turn auto-deploy off, then deploy'],
                  ['disabled', 'Disable the Deploy button'],
                ] as [DeployNowBehavior, string][]).map(([v, label]) => (
                  <button key={v} aria-pressed={settings.deployNowBehavior === v}
                          onClick={() => patch({ deployNowBehavior: v })}>
                    <strong>{label}</strong>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {pendingShow && (
        <ConfirmDialog
          title={`Load "${pendingShow.label}"?`}
          body="This replaces every presenter, channel, cue and assignment currently on the board. It cannot be undone."
          confirmLabel="Replace the board"
          danger
          onConfirm={async () => {
            const show = pendingShow.file;
            setPendingShow(null);
            try {
              const r = await api.importShow(identity.code, show);
              notify(`Loaded ${r.name}: ${r.presenters} presenters, ` +
                `${r.channels} channels, ${r.cues} cues.`);
            } catch (err) { notify((err as Error).message, 'error'); }
          }}
          onCancel={() => setPendingShow(null)}
        />
      )}

      {confirmEnd && (
        <ConfirmDialog
          title="End this show for everyone?"
          body="Every connected device is disconnected and the board is closed. The show data stays saved."
          confirmLabel="End show"
          danger
          onConfirm={async () => {
            setConfirmEnd(false);
            try {
              await api.endSession(identity.code);
              setIdentity(null);
            } catch (err) { notify((err as Error).message, 'error'); }
          }}
          onCancel={() => setConfirmEnd(false)}
        />
      )}
    </div>
  );
}
